import { Field, Form, Formik } from 'formik'
import React from 'react'

import { Dashboard, PageLayout } from '@components/common'
import { LeavingGuard } from '@components/utils/LeavingGuard'
import { SECTION_NAMES } from '@constants/sectionNames'
import {
  BackButton,
  ButtonClean,
  HeadContent,
  Input,
  SaveShortcutListener,
  Subsection,
} from '@fourthwall/components'
import { ConfirmBar } from '@modules/Dashboard/modules/ConfirmBar'
import { routing } from '@utils/routing'

import { useGoogleAnalyticsConfigurationQuery } from '../../../../../Analytics/pages/AnalyticsOverview/modules/GoogleAnalytics'
import * as S from './AnalyticsTrackingPixels.styled'
import { validationSchema } from './AnalyticsTrackingPixels.validationSchema'
import { FacebookPixelField } from './components/FacebookPixelField'
import { useAnalyticsTrackingPixels } from './hooks/useAnalyticsTrackingPixels'

const title = 'Tracking pixels'

export const AnalyticsTrackingPixels = () => {
  const { initialValues, onSubmit } = useAnalyticsTrackingPixels()
  const { googleAnalyticsConfigurationQuery } = useGoogleAnalyticsConfigurationQuery()
  const isGoogleAnalyticsDisabled = googleAnalyticsConfigurationQuery.data?.type === 'CONNECTED'

  return (
    <>
      <HeadContent title={title} titleTemplate={title} />
      <PageLayout>
        <Dashboard.ItemViewHeader
          name={title}
          backButton={<BackButton label={SECTION_NAMES.analytics} to={routing.analyticsV2.self} />}
        />

        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={onSubmit}
          enableReinitialize
        >
          {({ dirty, isValid, isSubmitting, submitForm, resetForm }) => (
            <Form>
              <Subsection title="Google Analytics Pixel">
                <S.SectionDescription>
                  Google Analytics is a free analytics tool that gives deep insight into your
                  website traffic.{' '}
                  <ButtonClean
                    label="Learn more"
                    href={routing.external.helpCenter.googleTrackingPixels}
                    target="_blank"
                  />
                </S.SectionDescription>
                <S.SectionInput>
                  <Field
                    component={Input}
                    name="googleAnalyticsTrackingId"
                    label="Google Analytics 4 Tracking ID"
                    suggestion="Code beginning with G- found at the end of the Google Analytics setup process."
                    disabled={isGoogleAnalyticsDisabled}
                  />
                </S.SectionInput>
              </Subsection>

              <Subsection title="Facebook Pixel">
                <S.SectionDescription>
                  Facebook Pixel helps you track conversions and click through rates from Facebook
                  and Instagram ads.{' '}
                  <ButtonClean
                    label="Learn more"
                    href={routing.external.helpCenter.metaTrackingPixels}
                    target="_blank"
                  />
                </S.SectionDescription>
                <S.SectionInput>
                  <FacebookPixelField />
                </S.SectionInput>
              </Subsection>

              <Subsection title="TikTok Pixel">
                <S.SectionDescription>
                  TikTok Pixel helps you track conversions and click through rates from TikTok ads.
                </S.SectionDescription>
                <S.SectionInput>
                  <Field
                    component={Input}
                    name="tiktokPixelId"
                    label="TikTok Pixel ID"
                    suggestion="Pixel ID found in Pixel code in the TikTok Pixel setup process."
                  />
                </S.SectionInput>
              </Subsection>

              <SaveShortcutListener disabled={!dirty} onSave={submitForm} />
              <ConfirmBar
                isOpen={dirty}
                isLoading={isSubmitting}
                onConfirm={submitForm}
                onCancel={resetForm}
              />
              <LeavingGuard when={dirty} isValid={isValid} onSave={submitForm} />
            </Form>
          )}
        </Formik>
      </PageLayout>
    </>
  )
}
